import React, { useState, useEffect } from 'react'

import * as StringFormatter from '../../../../global/formatters/string-formatter.js'
import { getRelativeBasePath } from '../../../../global/environments/path.js'

import { getVariableFundsData } from '../InvestmentProductsDataSource.js'
import FilterHeader from '../../../../components/PageHeader/FilterHeader.jsx'
import Table from '../../../../components/Table/Table.jsx'
import { stickyOnScroll } from '../helpers/InvestmentProductsHelper.js'
import '../InvestmentProducts.css'

export function VariableFunds() {
  const [funds, setFunds] = useState([])
  const [fundsFiltered, setFundsFiltered] = useState([])
  const [selectedFundSearch, setFundSearch] = useState('')
  const [selectedPerformanceFrequency, setSelectedPerformanceFrequency] =
    useState('Monthly')
  const [isFilterOn, setIsFilterOn] = useState(false)
  const [tableHeaderTop, setTableHeaderTop] = useState()

  const getAspect = (section) => {
    const aspects = [
      {
        name: '#variable-funds',
        info: {
          tag: selectedPerformanceFrequency,
          selector: [
            {
              name: 'value',
              isArray: false,
            },
            {
              name: 'variableProducts',
              isArray: false,
            },
          ],
          getData: getVariableFundsData,
          renderOptions: () => {
            return (
              <>
                <div className="u-flex u-sm-flexCol u-flexAlignItemsCenter u-sm-flexAlignItemsStart u-marginVert4gu">
                  {renderPerformanceOptions()}
                </div>
                <div className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween">
                  <div className="Form-label">
                    Displaying{' '}
                    <span className="u-textBold">
                      {isFilterOn
                        ? (fundsFiltered && fundsFiltered.length) || 0
                        : (funds && funds.length) || 0}
                    </span>{' '}
                    product(s)
                  </div>
                  <div className="u-sm-hidden u-flex u-flexAlignItemsCenter">
                    {renderDownloadOptions()}
                  </div>
                </div>
              </>
            )
          },
          columnSummaries: [
            {
              name: 'headerSummary0',
              colSpan: 2,
              content: () => '',
            },
            {
              name: 'headerSummary1',
              colSpan: 3,
              content: () => {
                return (
                  <span className="u-textSizeMsrm1">
                    Cumulative Total Returns (%)
                  </span>
                )
              },
            },
            {
              name: 'headerSummary2',
              colSpan: 6,
              content: () => {
                return (
                  <span className="u-textSizeMsrm1">
                    Average Annual Total Returns (%)
                  </span>
                )
              },
            },
            {
              name: 'headerSummary3',
              colSpan: 3,
              content: () => {
                return (
                  <span className="u-textSizeMsrm1">
                    Expense Ratio
                    {funds.length > 0 ? ' as of ' : ''}
                    {funds.length > 0
                      ? StringFormatter.formatDateStandard(
                          funds[0].expenseAsOfDate
                        )
                      : ''}
                  </span>
                )
              },
            },
          ],
          columns: [
            {
              name: 'name',
              className: 'Table-header',
              ariaLabel: 'Fund Name',
              content: <>Name</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingRight3gu u-textSizeMsrm1',
                sort: 'name',
                sortType: 'string',
              },
              cell: {
                content: (fund) => (
                  <a
                    className="u-textBold u-sm-textNone"
                    href={
                      getRelativeBasePath() +
                      `/investment-products/variable-products/${StringFormatter.urlSafe(
                        fund.name
                      )}/Class-${fund.className}/details/?cusip=${fund.cusip}`
                    }
                  >
                    {fund.name}
                  </a>
                ),
              },
            },
            {
              name: 'className',
              className: 'Table-header bordered',
              ariaLabel: 'Class',
              content: <>Class</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingRight3gu u-textSizeMsrm1',
                sort: 'className',
                sortType: 'number',
              },
              cell: {
                className: 'bordered-right',
                content: (fund) => (
                  <div className="u-textNoWrap">
                    {fund.className.trim() === ''
                      ? 'Shares'
                      : 'Class ' + fund.className}
                  </div>
                ),
              },
            },
            {
              name: 'puvOneMonth',
              className: 'Table-header u-textRight',
              ariaLabel: 'Cumulative, 1 month: activate to sort column',
              content: <>1 mo</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'puvOneMonth',
                sortType: 'number',
              },
              cell: {
                className: 'u-textRight',
                content: (fund) => fund.puvOneMonth,
              },
            },
            {
              name: 'puvThreeMonth',
              className: 'Table-header u-textRight',
              ariaLabel: 'Cumulative, 3 month: activate to sort column',
              content: <>3 mo</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'puvThreeMonth',
                sortType: 'number',
              },
              cell: {
                className: 'u-textRight',
                content: (fund) => fund.puvThreeMonth,
              },
            },
            {
              name: 'ytd',
              className: 'Table-header u-textRight bordered',
              ariaLabel: 'YTD: activate to sort column',
              content: <>YTD</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'ytd',
                sortType: 'number',
              },
              cell: {
                className: 'bordered-right u-textRight',
                content: (fund) => fund.ytd,
              },
            },
            {
              name: 'puvOneYear',
              className: 'Table-header u-textRight',
              ariaLabel: '1 Year: activate to sort column',
              content: <>1 Yr</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'puvOneYear',
                sortType: 'number',
              },
              cell: {
                className: 'u-textRight',
                content: (fund) => fund.puvOneYear,
              },
            },
            {
              name: 'puvThreeYear',
              className: 'Table-header u-textRight',
              ariaLabel: '3 Year: activate to sort column',
              content: <>3 Yr</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'puvThreeYear',
                sortType: 'number',
              },
              cell: {
                className: 'u-textRight',
                content: (fund) => fund.puvThreeYear,
              },
            },
            {
              name: 'puvFiveYear',
              className: 'Table-header u-textRight',
              ariaLabel: '5 Year: activate to sort column',
              content: <>5 Yr</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'puvFiveYear',
                sortType: 'number',
              },
              cell: {
                className: 'u-textRight',
                content: (fund) => fund.puvFiveYear,
              },
            },
            {
              name: 'puvTenYear',
              className: 'Table-header u-textRight',
              ariaLabel: '10 Year: activate to sort column',
              content: <>10 Yr</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'puvTenYear',
                sortType: 'number',
              },
              cell: {
                className: 'u-textRight',
                content: (fund) => fund.puvTenYear,
              },
            },
            {
              name: 'puvSinceInception',
              className: 'Table-header u-textNoWrap u-textRight',
              ariaLabel: 'Since Inception: activate to sort column',
              content: (
                <>
                  Since Fund
                  <br />
                  Incept. *
                </>
              ),
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'puvSinceInception',
                sortType: 'number',
              },
              cell: {
                className: 'u-textRight',
                content: (fund) => fund.puvSinceInception,
              },
            },
            {
              name: 'inceptionDt',
              className: 'Table-header u-textNoWrap u-textRight bordered',
              ariaLabel: 'Inception Date: activate to sort column',
              content: (
                <>
                  Fund Incept.
                  <br />
                  Date
                </>
              ),
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'inceptionDt',
                sortType: 'date',
              },
              cell: {
                className: 'u-textNoWrap bordered-right u-textRight',
                content: (fund) =>
                  StringFormatter.formatDateStandard(fund.inceptionDt),
              },
            },
            {
              name: 'grossExp',
              className: 'Table-header u-textRight',
              ariaLabel: 'Net: activate to sort column',
              content: <>Gross</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'grossExp',
                sortType: 'number',
              },
              cell: {
                className: 'u-textRight',
                content: (fund) => fund.grossExp,
              },
            },
            {
              name: 'netExp',
              className: 'Table-header u-textRight',
              ariaLabel: 'Gross: activate to sort column',
              content: <>Net</>,
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight u-textSizeMsrm1',
                sort: 'netExp',
                sortType: 'number',
              },
              cell: {
                className: 'u-textRight',
                content: (fund) => fund.netExp,
              },
            },
            {
              name: 'waiverDt',
              className: 'Table-header u-textRight',
              ariaLabel: 'Fund Waiver Expiration Date: activate to sort column',
              content: (
                <>
                  Waiver Expiration
                  <br />
                  Date
                </>
              ),
              button: {
                className:
                  'Table-headerNode u-padding2gu u-paddingLeft3gu u-textRight',
                sort: 'waiverDt',
                sortType: 'date',
              },
              cell: {
                className: ' u-textNoWrap bordered u-textRight',
                content: (fund) =>
                  fund.expenseRatioWaiverDate
                    ? StringFormatter.formatDateStandard(
                        fund.expenseRatioWaiverDate
                      )
                    : '-',
              },
            },
          ],
        },
      },
    ]

    return aspects.find((a) => a.name === section).info
  }

  useEffect(() => {
    initializeFunds()
  }, [selectedPerformanceFrequency])

  useEffect(() => {
    initializeFundsFiltered()
  }, [selectedFundSearch])

  useEffect(() => {
    if (funds && funds.length > 0) {
      window.prerenderReady = true
    }
  }, [funds])

  useEffect(() => {
    stickyOnScroll(setTableHeaderTop)
  }, [])

  const downloadFunds = (type) => {
    let url = ''

    url += '/cmg.svc/exportsummary'
    url += '?fundGroupName=VariableProducts'
    url += '&className='
    url += '&fileType=' + type

    window.location = url
  }

  const initializeFundsFiltered = (fundsArray) => {
    const _funds = fundsArray || funds

    if (selectedFundSearch) {
      const _fundsFiltered = _funds.filter((fund) => {
        // Search
        const fundSearchTest =
          !selectedFundSearch ||
          fund.name.toLowerCase().indexOf(selectedFundSearch.toLowerCase()) > -1

        // Does fund meet all tests?
        return fundSearchTest
      })

      setIsFilterOn(true)

      setFundsFiltered(_fundsFiltered)
    } else {
      setIsFilterOn(false)

      setFundsFiltered([])
    }
  }

  const initializeFunds = async () => {
    const aspect = getAspect('#variable-funds')

    const data = await aspect.getData(aspect.tag)

    const _funds = aspect.selector.reduce(
      (prev, curr) => (curr.isArray ? prev[curr.name][0] : prev[curr.name]),
      data
    )

    setFunds(_funds)

    initializeFundsFiltered(_funds)
  }

  const onChangeFundSearch = (event) => {
    setFundSearch(event.currentTarget.value)
  }

  const onChangePerformanceFrequencyDropdown = (event) => {
    setSelectedPerformanceFrequency(
      event.currentTarget.selectedOptions[0].value
    )
  }

  const onClickDownloadCSV = (event) => {
    event.preventDefault()

    downloadFunds('csv')
  }

  const onClickDownloadTSV = (event) => {
    event.preventDefault()

    downloadFunds('tsv')
  }

  const renderPerformanceOptions = () => {
    return (
      <div className="u-flex u-flexAlignItemsCenter u-sm-flexCol u-sm-flexAlignItemsStart u-sm-flexAlignItemsStretch">
        Performance
        <div className="Dropdown u-marginLeft2gu u-sm-marginLeft0 u-sm-marginVert1gu">
          <label className="u-hiddenVisually" htmlFor="frequency_dropdown">
            Frequency
          </label>
          <select
            id="frequency_dropdown"
            className="Dropdown-menu u-sm-marginBottom3gu"
            value={selectedPerformanceFrequency}
            onChange={onChangePerformanceFrequencyDropdown}
          >
            <option value="Monthly">MONTHLY</option>
            <option value="Quarterly">QUARTERLY</option>
          </select>
        </div>
        <div className="u-marginLeft2gu u-sm-marginLeft0">
          {funds.length > 0
            ? 'All data as of ' +
              StringFormatter.formatDateStandard(funds[0].asOfDate) +
              ' unless otherwise indicated.'
            : ''}
        </div>
      </div>
    )
  }

  const renderDownloadOptions = () => {
    return (
      <div>
        <button
          className="Button Button--tertiary u-padding2gu u-textNoTransform u-textSizeMsrm1"
          onClick={onClickDownloadCSV}
          data-qa-automation="download-csv"
        >
          Download .CSV
        </button>
        |
        <button
          className="Button Button--tertiary u-padding2gu u-textNoTransform u-textSizeMsrm1"
          onClick={onClickDownloadTSV}
          data-qa-automation="download-tsv"
        >
          Download .TSV
        </button>
      </div>
    )
  }

  const aspect = getAspect('#variable-funds')

  const rowsData = isFilterOn ? fundsFiltered : funds

  return (
    <div className="investment-products">
      {/* HEADER */}
      <div className="u-paddingVert2gu">
        <div className="u-sizeConstrained Content">
          {/* FILTER / SEARCH */}
          <div className="u-md-size1of2 u-lg-size1of2">
            <FilterHeader
              placeholder="Search variable products"
              onSearch={onChangeFundSearch}
            />
          </div>
          {/* OPTIONS */}
          <div>{aspect.renderOptions()}</div>
          <div className="InvestmentProductTable">
            <Table
              columns={aspect.columns}
              columnSummaries={aspect.columnSummaries}
              rows={rowsData?.length > 0 ? rowsData : null}
              mode={'no-stripe'}
              scrollOffset={tableHeaderTop}
            />
          </div>
        </div>
      </div>
      <div id="ampf-trace-loaded-page"></div>
    </div>
  )
}
